import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Artifact,
  ArtifactClose,
  ArtifactContent,
} from '@cotera/client/v0/components/ai-elements/artifact';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@cotera/client/v0/components/ui/resizable';
import {
  Check,
  ChevronDown,
  LayoutGrid,
  MessageSquareIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Skeleton } from '@cotera/client/v0/components/ui/skeleton';
import type { ArtifactDescriptor } from './artifacts';
import { getArtifactRenderer } from './artifacts';
import { ArtifactIcon } from './artifact-snippet';
import {
  ArtifactPanelProvider,
  PanelUnifiedChromeProvider,
  useArtifactPanel,
  usePanelUnifiedChrome,
  type ArtifactPanelContextValue,
  type ArtifactPanelViewEntry,
  type OpenArtifactRequest,
  type PanelUnifiedChrome,
  type PanelHost,
} from './artifact-panel-context';
import { UnifiedPanelBar } from './unified-panel-bar';
import { ArtifactsIndexCard } from './artifacts-index-card';
import { ARTIFACTS_INDEX_GUTTER } from './chat-layout';
import { useArtifactsIndexVisibility } from './artifacts-index-visibility';
import {
  popViewEntry,
  pushViewEntry,
  resolveDeepestView,
} from './artifact-panel-navigation';
import { useIsBelowMd } from '@cotera/client/app/hooks';
import {
  ModelTargetScope,
  ModelScopeFactory,
  useStatefullyAwareInScopeModel,
  useWatchableValue,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { useArtifactTitle } from './use-artifact-title';
import { cn } from '@cotera/client/v0/lib/utils';
import type { ImperativePanelGroupHandle } from 'react-resizable-panels';
import { ArtifactPanelModel } from './artifact-panel.model';
import { ChatArtifactModel } from './chat-artifact.model';
import { artifactMatchesDirtyResource } from './artifact-dirty-state';
import { ArtifactDirtyDot } from './artifact-dirty-dot';
import {
  DirtyArtifactResourceIdsProvider,
  useDirtyArtifactResourceIds,
} from './use-dirty-artifact-resource-ids';
import {
  ArchivedArtifactIdsProvider,
  useArchivedArtifactIds,
} from './use-archived-artifact-ids';
import { ResourceNavigatorBridge } from '@cotera/client/app/v1/resources/navigation/resource-navigator-bridge';
import {
  ArtifactLinkStackView,
  isArtifactLinkViewPayload,
  type ArtifactLinkTarget,
} from '@cotera/client/app/v1/resources/navigation/artifact-links';

/**
 * How long the panel takes to open, close and expand.
 *
 * The four `duration-[320ms]` classes below have to match: Tailwind needs the
 * literal in the source to generate the utility, so the value cannot be
 * interpolated from here. Change one, change all five.
 */
const ARTIFACT_PANEL_EXPAND_MS = 320;
const PANEL_FLEX_TRANSITION = `flex-grow ${ARTIFACT_PANEL_EXPAND_MS}ms ease-out`;

/**
 * `react-resizable-panels` sizes panels by writing `flex-grow`, and it writes
 * that value instantly for every programmatic layout change — `setLayout`,
 * `collapse()` and `expand()` alike. Putting a transition on the property for
 * the duration of one such change is what turns the jump into a slide.
 *
 * The transition has to be removed afterwards: left on, it would also animate
 * the `flex-grow` writes that happen on every frame of a *drag*, so the panel
 * would lag the cursor.
 */
const applyPanelTransitions = (groupId: string): void => {
  const groupElement = document.querySelector(
    `[data-panel-group-id="${groupId}"]`
  );
  groupElement?.querySelectorAll('[data-panel]').forEach((panel) => {
    (panel as HTMLElement).style.transition = PANEL_FLEX_TRANSITION;
  });
};

const clearPanelTransitions = (groupId: string): void => {
  const groupElement = document.querySelector(
    `[data-panel-group-id="${groupId}"]`
  );
  groupElement?.querySelectorAll('[data-panel]').forEach((panel) => {
    (panel as HTMLElement).style.removeProperty('transition');
  });
};

/**
 * Floor for the artifact panel, as a percentage of the split.
 *
 * Paired with `collapsible` + `collapsedSize={0}`: dragging below it snaps the
 * panel shut rather than leaving a sliver too narrow to use. Someone dragging
 * the split that far closed is closing it, so the panel treats it as the same
 * gesture as the bar's hide button — the artifacts stay open and the
 * artifacts index card offers the way back.
 *
 * Set against the bar, which is the first thing to break: the switcher, the
 * resource's own actions and the window controls stop fitting together well
 * before the content does. At 25% the panel was still draggable to a width
 * where all of that was crushed but the panel stayed open — technically
 * resizable, practically unusable. Closing earlier is the better failure.
 *
 * From the 50% default that leaves a useful amount of travel and no dead zone
 * at the end of it.
 */
const MIN_ARTIFACT_PANEL_PERCENT = 45;
const NULL_EXPANDED_WATCHABLE = Watchable.fromValue(false);

/** Imperative handle so callers (e.g. the chat wrapper) can focus an artifact. */
export type ArtifactSidePanelHandle = {
  focusArtifact: (artifactId: string) => void;
};

type ArtifactSidePanelProps = {
  /** The artifacts to show. The newest (last) is auto-focused when the set grows. */
  artifacts: ArtifactDescriptor[];
  /** Main content rendered beside the panel (chat or an editor). */
  children: React.ReactNode;
  defaultArtifactPanelSize?: number;
  /**
   * Narrowest the panel can be dragged before it snaps shut, as a percentage of
   * the split. Below this the bar has no room for the switcher, the resource's
   * actions and the window controls at once, so a narrower panel is not a
   * smaller panel — it is a broken one.
   */
  minArtifactPanelSize?: number;
  /** Mobile: icon for the "primary view" (non-artifact) tab. Defaults to a chat bubble. */
  primaryTabIcon?: React.ReactNode;
  /**
   * When provided, closing the artifact calls this instead of just collapsing the
   * panel — lets a caller fully remove the artifact (e.g. the transient editor panel).
   */
  onRequestClose?: () => void;
  /**
   * Add/remove transient artifacts. Wired by the conversation-backed wrapper,
   * which owns the transient list; when provided, the panel exposes
   * `openArtifact`/`closeArtifact` on its context so views rendered inside an
   * artifact can open or dismiss siblings (e.g. a workflow node's config).
   */
  onOpenArtifact?: (request: OpenArtifactRequest) => void;
  onRemoveArtifact?: (artifactId: string) => void;
  /**
   * Hide the split border and pin the resize grip to the artifact card's left
   * edge (used by standalone editor pages).
   */
  artifactCardResizeHandle?: boolean;
  /**
   * Whether the panel lets itself in when an artifact turns up. Default true,
   * which is right where the only way an artifact gets here is the user asking
   * for one (the standalone editor pages' transient panel) — the request and the
   * panel opening are the same act.
   *
   * The chat passes false. There an artifact arriving says nothing about whether
   * the reader wants half their window given over to it: a chat is reopened with
   * everything it ever produced still attached, so "an artifact appeared" fires
   * on load for work finished days ago. The artifacts index card is the standing
   * answer to what is waiting — count, unread dot, and one click into any of it —
   * and it costs a corner rather than a pane.
   */
  autoOpenOnArtifact?: boolean;
  /**
   * The surface the panel belongs to. Carries no name — the trail omits the host
   * entirely, since the page bar one row above already gives it — only the
   * access control that is mirrored into every artifact's bar. See
   * {@link PanelUnifiedChrome.host}.
   */
  host?: PanelHost;
  /**
   * Render one artifact as the whole page instead of half of a split.
   *
   * The panel keeps the list, the renderers and the model scopes — it is the
   * same artifact, mounted the same way — and drops the chat pane, the resize
   * handle and the switcher. See {@link PanelUnifiedChrome.focusView}.
   */
  focusView?: {
    artifactId: string;
    onBack: () => void;
    onSelectArtifact: (artifactId: string, artifactType: string) => void;
    onShare: (artifact: ArtifactDescriptor) => void;
  };
  /**
   * Route to one artifact's own page; see {@link PanelUnifiedChrome.openFocusView}.
   *
   * Takes the type as well as the id because the destination is a URL, and what
   * a URL has to carry to reproduce an artifact depends on what kind it is.
   */
  onOpenFocusView?: (artifactId: string, artifactType: string) => void;
  /**
   * The artifact list has not been read from its source yet, so an empty list
   * means "not known" rather than "none". Only the focus view cares: it is
   * the one surface that has to tell a missing artifact from a pending one.
   */
  artifactsLoading?: boolean;
};

/**
 * A focus view whose chat has not finished handing over its artifact list.
 *
 * The list arrives in two steps — the conversation loads, then its `artifacts()`
 * promise resolves — and until both have happened an empty list says nothing
 * about whether this artifact exists. Without this state the view read that
 * silence as absence and told people their artifact had been removed, on every
 * load, before the chat had even been asked.
 */
/**
 * Whether a reported split layout is one the reader could have left the divider
 * at — the only kind worth remembering as "the width to come back to".
 *
 * Both panes have to have width. A layout with the chat pane at 0 is never a
 * position anyone dragged to: the chat's `minSize` is 30 during a drag, and it
 * reaches 0 only in the two states that take the artifact full-bleed — the
 * in-place expand, which sets `[0, 100]` outright, and the frame on the way out
 * of focus where the split remounts with the artifact still filling the pane
 * before it animates back.
 *
 * Recording those was self-defeating, because the ref this guards is what the
 * *next* reopen restores. Leaving focus wrote `[0, 100]`, so the first return
 * was right and every one after it handed back an artifact at full width with
 * the conversation squeezed to nothing. A single in-place expand did the same
 * to the next reopen of a collapsed panel, with no focus view involved.
 */
export const isRestingSplitLayout = (layout: readonly number[]): boolean =>
  (layout[0] ?? 0) > 0 && (layout[1] ?? 0) > 0;

const ArtifactFocusViewLoading: React.FC = () => (
  <div className="flex h-full w-full items-center justify-center p-8">
    <div className="flex w-full max-w-md flex-col gap-3">
      <Skeleton className="h-6 w-1/3 rounded bg-coco-accent" />
      <Skeleton className="h-4 w-full rounded bg-coco-accent" />
      <Skeleton className="h-4 w-5/6 rounded bg-coco-accent" />
    </div>
  </div>
);

/**
 * A focus view whose artifact is not in the conversation: removed since the link
 * was made, or never in this chat at all.
 *
 * Deliberately not a redirect. Bouncing to the chat would leave someone who
 * followed a link staring at a conversation with no explanation of what they
 * were meant to see, and would make a mistyped id indistinguishable from a
 * working one.
 */
const ArtifactFocusViewMissing: React.FC<{ onBack: () => void }> = ({
  onBack,
}) => (
  <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-8 text-center">
    <p className="text-sm font-medium text-foreground">
      This artifact is not in this chat
    </p>
    <p className="text-sm text-muted-foreground">
      It may have been removed since the link was shared.
    </p>
    <Button type="button" variant="outline" size="sm" onClick={onBack}>
      Back to the chat
    </Button>
  </div>
);

const ArtifactFallback: React.FC<{ type: string }> = ({ type }) => (
  <div className="text-sm text-muted-foreground">
    No renderer for artifact type &quot;{type}&quot;
  </div>
);

const ArtifactLabel: React.FC<{
  artifact: ArtifactDescriptor;
  showDirtyIndicator?: boolean;
  dirtyIndicatorPlacement?: 'inline' | 'select-item';
}> = ({
  artifact,
  showDirtyIndicator = false,
  dirtyIndicatorPlacement = 'inline',
}) => {
  const title = useArtifactTitle(artifact);
  const dirtyResourceIds = useDirtyArtifactResourceIds();
  const isDirty =
    showDirtyIndicator &&
    artifactMatchesDirtyResource(artifact, dirtyResourceIds);

  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <span className="truncate text-foreground">{title}</span>
      {isDirty ? (
        <ArtifactDirtyDot
          className={cn(
            dirtyIndicatorPlacement === 'select-item' &&
              'absolute right-2 top-1/2 -translate-y-1/2'
          )}
        />
      ) : null}
    </span>
  );
};

/**
 * Generic artifact side panel: a resizable split (desktop) / tabbed view (mobile)
 * that renders one of `artifacts` beside `children`. Knows nothing about chat or
 * conversations — see `ChatWithArtifactPanel` for the conversation-backed wrapper.
 */
export const ArtifactSidePanel = forwardRef<
  ArtifactSidePanelHandle,
  ArtifactSidePanelProps
>((props, ref) => {
  const createModels = useCallback(() => [new ArtifactPanelModel()], []);
  return (
    <ModelScopeFactory createModels={createModels} deps={[]}>
      {/* Above both the mobile and desktop branches, so the artifact label, the
          bar's switcher and the artifacts index card share one subscription.
          The archived set is published from the same place and for the same
          reason — the index card unmounts whenever the Canvas is open, so a
          hook of its own would be cold exactly when the switcher wanted it. */}
      <DirtyArtifactResourceIdsProvider>
        <ArchivedArtifactIdsProvider artifacts={props.artifacts}>
          <ArtifactSidePanelInner ref={ref} {...props} />
        </ArchivedArtifactIdsProvider>
      </DirtyArtifactResourceIdsProvider>
    </ModelScopeFactory>
  );
});

ArtifactSidePanel.displayName = 'ArtifactSidePanel';

const ArtifactSidePanelInner = forwardRef<
  ArtifactSidePanelHandle,
  ArtifactSidePanelProps
>(
  (
    {
      artifacts,
      children,
      defaultArtifactPanelSize = 50,
      minArtifactPanelSize = MIN_ARTIFACT_PANEL_PERCENT,
      primaryTabIcon,
      onRequestClose,
      onOpenArtifact,
      onRemoveArtifact,
      artifactCardResizeHandle = false,
      autoOpenOnArtifact = true,
      host,
      focusView,
      onOpenFocusView,
      artifactsLoading = false,
    },
    ref
  ) => {
    const isMobile = useIsBelowMd();
    // Nothing to expand *into* when the artifact already is the page.
    const canExpand =
      !isMobile && artifacts.length > 0 && focusView === undefined;

    const artifactPanelModel =
      useStatefullyAwareInScopeModel(ArtifactPanelModel);
    const isExpanded = useWatchableValue(
      artifactPanelModel?.isExpandedWatchable ?? NULL_EXPANDED_WATCHABLE
    );

    useEffect(() => {
      artifactPanelModel?.setCanExpand(canExpand);
    }, [artifactPanelModel, canExpand]);

    const toggleExpanded = useCallback(() => {
      artifactPanelModel?.toggleExpanded();
    }, [artifactPanelModel]);

    // The model's `expand` already guards `canExpand` and no-ops when open, so
    // callers wanting the room don't have to read `isExpanded` first (and can't
    // collapse the panel by acting on a stale read of it).
    const expand = useCallback(() => {
      artifactPanelModel?.expand();
    }, [artifactPanelModel]);

    const [activeTab, setActiveTab] = useState<string | null>(() =>
      artifacts.length > 0
        ? artifacts[artifacts.length - 1]?.artifactId ?? null
        : null
    );
    const previousArtifactIdsRef = useRef<string[]>(
      artifacts.map((artifact) => artifact.artifactId)
    );
    // Artifacts added in the background (openArtifact with `focus: false`, e.g. an
    // agent's referenced documents). They still appear in the list but must not
    // steal focus via the "auto-focus the newest artifact" effect below.
    const backgroundArtifactIdsRef = useRef<Set<string>>(new Set());
    // Artifacts the user has focused or selected at least once. Graduation is sticky:
    // a re-sync from openArtifact(`focus: false`) must not drag an engaged tab back
    // into the background set (or removing its @-mention could close a tab the user
    // had opened). Cleared only when the artifact is removed entirely.
    const engagedArtifactIdsRef = useRef<Set<string>>(new Set());

    const [viewStacks, setViewStacks] = useState<
      Map<string, ArtifactPanelViewEntry[]>
    >(() => new Map());

    useEffect(() => {
      const artifactIds = artifacts.map((artifact) => artifact.artifactId);
      if (artifactIds.length === 0) {
        previousArtifactIdsRef.current = [];
        setActiveTab(null);
        return;
      }

      const previousArtifactIdSet = new Set(previousArtifactIdsRef.current);
      // Background artifacts (added with `focus: false`) never drive auto-focus, so
      // surfacing an agent's referenced docs doesn't yank the user off the agent.
      const isFocusable = (artifactId: string): boolean =>
        !backgroundArtifactIdsRef.current.has(artifactId);
      const hasNewFocusableArtifact = artifactIds.some(
        (artifactId) =>
          !previousArtifactIdSet.has(artifactId) && isFocusable(artifactId)
      );
      const newestFocusableArtifactId =
        [...artifactIds].reverse().find(isFocusable) ??
        artifactIds[artifactIds.length - 1] ??
        null;

      setActiveTab((prev) => {
        if (hasNewFocusableArtifact) {
          return newestFocusableArtifactId;
        }
        if (prev === null || !artifactIds.includes(prev)) {
          return newestFocusableArtifactId;
        }
        return prev;
      });
      previousArtifactIdsRef.current = artifactIds;
    }, [artifacts]);

    // Drop stacks for artifacts that are no longer in the panel.
    useEffect(() => {
      const artifactIds = new Set(
        artifacts.map((artifact) => artifact.artifactId)
      );
      setViewStacks((prev) => {
        let changed = false;
        const next = new Map(prev);
        for (const id of next.keys()) {
          if (!artifactIds.has(id)) {
            next.delete(id);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, [artifacts]);

    const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);
    const artifactPanelRef =
      React.useRef<React.ElementRef<typeof ResizablePanel>>(null);
    // The artifact panel's content box, pinned to a fixed width during width
    // transitions — see `pinContentWidth`.
    const contentWrapperRef = useRef<HTMLDivElement | null>(null);
    // Desktop: the split panel was collapsed (header "X", or dragged shut) while
    // artifacts are still open. Drives the composer's "show artifacts" affordance.
    const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
    // The panel unmounts entirely when the last artifact goes away and comes back
    // at its default size, so the collapsed flag must not outlive it.
    useEffect(() => {
      if (artifacts.length === 0) {
        setIsPanelCollapsed(false);
      }
    }, [artifacts.length]);
    const preExpandLayoutRef = useRef<number[] | null>(null);
    const [isLayoutAnimating, setIsLayoutAnimating] = useState(false);
    const [isManualResizing, setIsManualResizing] = useState(false);
    const previousIsExpandedRef = useRef(isExpanded);
    const pendingExpandLayoutRef = useRef(isExpanded);
    // Set when the close control is hit while expanded: the panel isn't
    // collapsible in expanded layout, so we drop expanded first and collapse it
    // as soon as the panel can take it (see the expanded-state effect below).
    const pendingCloseRef = useRef(false);

    const defaultSplitLayout = useMemo(
      () => [100 - defaultArtifactPanelSize, defaultArtifactPanelSize],
      [defaultArtifactPanelSize]
    );

    // Arms the flex-grow transition for one imperative layout change, then takes
    // it back off so dragging stays 1:1 with the cursor. `collapse()` and
    // `expand()` write the new size synchronously, so this has to be called
    // immediately before them.
    const transitionTimeoutRef = useRef<number | undefined>(undefined);
    const animateNextLayoutChange = useCallback(() => {
      const group = panelGroupRef.current;
      if (group === null) {
        return;
      }
      const groupId = group.getId();
      applyPanelTransitions(groupId);
      if (transitionTimeoutRef.current !== undefined) {
        window.clearTimeout(transitionTimeoutRef.current);
      }
      transitionTimeoutRef.current = window.setTimeout(() => {
        clearPanelTransitions(groupId);
        transitionTimeoutRef.current = undefined;
      }, ARTIFACT_PANEL_EXPAND_MS);
    }, []);

    useEffect(
      () => () => {
        if (transitionTimeoutRef.current !== undefined) {
          window.clearTimeout(transitionTimeoutRef.current);
        }
      },
      []
    );

    // The last layout in which *both* panes had width — the split as the reader
    // last left it. Reopening and returning from focus animate toward this
    // rather than discovering a target after the fact.
    const lastOpenLayoutRef = useRef<number[] | null>(null);

    const handlePanelLayout = useCallback(
      (layout: number[]) => {
        if (!isExpanded && !isLayoutAnimating) {
          preExpandLayoutRef.current = layout;
        }
        if (isRestingSplitLayout(layout)) {
          lastOpenLayoutRef.current = layout;
        }
      },
      [isExpanded, isLayoutAnimating]
    );

    /**
     * Holds the artifact's contents at their *open* width for the duration of a
     * width transition, so the panel reveals a finished card instead of
     * stretching one into place.
     *
     * Animating `flex-grow` alone re-lays-out the card on every frame: text
     * rewraps, the bar's container queries fire, the list's columns resize. The
     * chat side resizing smoothly is the point of the animation; the artifact
     * side reflowing along with it is just the cost of how the width is driven.
     * Pinning the card to its final pixel width and clipping the panel
     * separates the two — the panel's edge sweeps across a card that is already
     * the right shape.
     *
     * `margin-left: auto` anchors the card to the panel's right edge, so it is
     * uncovered leftward, toward the chat, rather than growing from the left.
     *
     * Returns the undo. No-ops (returning a no-op undo) if anything needed is
     * missing, which leaves the plain flex-grow animation as the fallback.
     */
    const pinContentWidth = useCallback((percent: number): (() => void) => {
      const noop = (): void => undefined;
      const group = panelGroupRef.current;
      const content = contentWrapperRef.current;
      if (group === null || content === null || percent <= 0) {
        return noop;
      }
      const groupEl = document.querySelector(
        `[data-panel-group-id="${group.getId()}"]`
      );
      if (groupEl === null) {
        return noop;
      }
      const width = (groupEl.getBoundingClientRect().width * percent) / 100;
      if (width <= 0) {
        return noop;
      }
      const panelEl = content.parentElement;
      const previousOverflow = panelEl?.style.overflow ?? '';
      content.style.width = `${width}px`;
      content.style.marginLeft = 'auto';
      if (panelEl !== null) {
        panelEl.style.overflow = 'hidden';
      }
      return () => {
        content.style.removeProperty('width');
        content.style.removeProperty('margin-left');
        if (panelEl === null) {
          return;
        }
        if (previousOverflow === '') {
          panelEl.style.removeProperty('overflow');
        } else {
          panelEl.style.overflow = previousOverflow;
        }
      };
    }, []);

    /**
     * Runs `change` as an animated width transition, with the card pinned at
     * whichever of the two widths is the open one — the target when opening,
     * the current when closing.
     *
     * `settleFirst` pins the card, waits two frames, and only then starts the
     * width moving. Opening needs it and closing does not, and the difference is
     * not the animation but what each one has to do underneath: a panel being
     * revealed has been sitting at zero width with its view hidden, so nothing
     * in it has ever been laid out. Starting the transition in the same frame
     * makes the browser lay out the whole card — its bar's container queries,
     * its content, whatever the artifact renders — while it is trying to paint
     * the first frames, and those frames are the ones that get dropped. Closing
     * animates a card that is already laid out, which is exactly why it looked
     * smoother.
     *
     * Two frames rather than one: the first lets React's commit land, the second
     * lets the layout it caused finish. The same double-rAF the expanded-layout
     * transition uses, for the same reason. Nothing is visible during them — the
     * panel is still at zero width and clipped.
     */
    const animateWidthChange = useCallback(
      (openPercent: number, change: () => void, settleFirst = false) => {
        const unpin = pinContentWidth(openPercent);
        const run = (): void => {
          animateNextLayoutChange();
          change();
          window.setTimeout(unpin, ARTIFACT_PANEL_EXPAND_MS);
        };
        if (!settleFirst) {
          run();
          return;
        }
        requestAnimationFrame(() => {
          requestAnimationFrame(run);
        });
      },
      [pinContentWidth, animateNextLayoutChange]
    );

    // Step 1: expanded state changed — arm the layout transition.
    useEffect(() => {
      if (artifacts.length === 0 || isMobile) {
        return;
      }
      if (previousIsExpandedRef.current === isExpanded) {
        return;
      }
      previousIsExpandedRef.current = isExpanded;
      // Closing from expanded: the panel is now collapsible again, so collapse it
      // straight to hidden instead of animating back to the split first.
      if (!isExpanded && pendingCloseRef.current) {
        pendingCloseRef.current = false;
        const panel = artifactPanelRef.current as
          | { collapse?: () => void }
          | null
          | undefined;
        if (typeof panel?.collapse === 'function') {
          // Closing straight out of the expanded layout: the card is at full
          // bleed, so that is the width to hold it at while the panel sweeps
          // shut.
          const openPercent = panelGroupRef.current?.getLayout()[1] ?? 100;
          animateWidthChange(openPercent, () => panel.collapse?.());
        }
        return;
      }
      pendingExpandLayoutRef.current = isExpanded;
      setIsLayoutAnimating(true);
    }, [isExpanded, artifacts.length, isMobile, animateWidthChange]);

    // Step 2: transition class is on the DOM — then animate flex sizes.
    useEffect(() => {
      if (!isLayoutAnimating) {
        return;
      }

      const group = panelGroupRef.current;
      if (group === null) {
        return;
      }

      const targetExpanded = pendingExpandLayoutRef.current;
      let timeoutId: number | undefined;
      const groupId = group.getId();

      const rafId = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          applyPanelTransitions(groupId);
          if (targetExpanded) {
            preExpandLayoutRef.current = group.getLayout();
            group.setLayout([0, 100]);
          } else {
            group.setLayout(preExpandLayoutRef.current ?? defaultSplitLayout);
          }
          timeoutId = window.setTimeout(() => {
            clearPanelTransitions(groupId);
            setIsLayoutAnimating(false);
          }, ARTIFACT_PANEL_EXPAND_MS);
        });
      });

      return () => {
        cancelAnimationFrame(rafId);
        clearPanelTransitions(groupId);
        if (timeoutId !== undefined) {
          window.clearTimeout(timeoutId);
        }
      };
    }, [isLayoutAnimating, defaultSplitLayout]);

    // Mobile-only: which tab is showing (primary vs artifacts).
    const [mobileViewMode, setMobileViewMode] = useState<'chat' | 'artifacts'>(
      'chat'
    );

    /**
     * Leaving focus puts you back where you were reading, in the pane you were
     * reading it in.
     *
     * Two things have to be true and neither was. While focused the address
     * decides what is on screen and `activeTab` is left alone
     * (`activeTabFromFocusView` overrules it anyway), so the split resumed
     * whatever tab it held *before* focus. And the split mounts shut in a chat
     * (`autoOpenOnArtifact` is false there, for good reasons — see the prop),
     * so closing focus dropped the reader onto the artifacts index card having
     * been mid-artifact a moment earlier.
     *
     * Adjusted during render rather than in an effect, and that is the whole
     * point: the split's `ResizablePanelGroup` unmounts while focused and
     * registers again on the way back, and registration recomputes the layout
     * from `defaultSize`. An imperative `expand()` from an effect is overwritten
     * by it — the same trap the opening-animation comment below is written
     * against. `startsClosed` is a default size, so it is the one thing late
     * enough to be the last word.
     */
    const focusedArtifactId = focusView?.artifactId ?? null;
    const previouslyFocusedRef = useRef(focusedArtifactId);
    const returningFromFocusRef = useRef(false);
    // Armed during render, not in the effect that consumes it: the split has to
    // *mount* with the chat pane's floor already lifted, or the first layout it
    // is given — the artifact still filling the pane, as focus left it — is
    // clamped to `minSize` and the animation starts from the wrong place.
    const animateOutOfFocusRef = useRef(false);
    const [isClosingFocus, setIsClosingFocus] = useState(false);
    if (previouslyFocusedRef.current !== focusedArtifactId) {
      const leaving = previouslyFocusedRef.current;
      previouslyFocusedRef.current = focusedArtifactId;
      returningFromFocusRef.current = focusedArtifactId === null;
      if (leaving !== null && focusedArtifactId === null) {
        setActiveTab(leaving);
        // The index card keys off this, and the group unmounting on the way
        // into focus can fire `onCollapse` on its way out — which would draw
        // the card over a split that is about to open. Returning from focus the
        // panel is open by construction, so say so.
        setIsPanelCollapsed(false);
        if (isMobile) {
          setMobileViewMode('artifacts');
        } else {
          animateOutOfFocusRef.current = true;
          setIsClosingFocus(true);
        }
      }
    }

    // When a new artifact appears on mobile, jump to the artifacts tab so the
    // user sees what was just created (mirrors desktop's panel auto-expand).
    // Background artifacts (added with `focus: false`, e.g. an agent's referenced
    // docs) are excluded, so surfacing them doesn't pull the user off their tab.
    const previousMobileArtifactIdsRef = useRef<string[]>(
      artifacts.map((artifact) => artifact.artifactId)
    );
    useEffect(() => {
      if (!isMobile) {
        previousMobileArtifactIdsRef.current = artifacts.map(
          (artifact) => artifact.artifactId
        );
        return;
      }
      const previousIds = new Set(previousMobileArtifactIdsRef.current);
      const hasNewFocusableArtifact = artifacts.some(
        (artifact) =>
          !previousIds.has(artifact.artifactId) &&
          !backgroundArtifactIdsRef.current.has(artifact.artifactId)
      );
      if (hasNewFocusableArtifact) {
        setMobileViewMode('artifacts');
      }
      previousMobileArtifactIdsRef.current = artifacts.map(
        (artifact) => artifact.artifactId
      );
    }, [artifacts, isMobile]);

    const handleClose = useCallback(() => {
      if (onRequestClose !== undefined) {
        onRequestClose();
        return;
      }
      if (isMobile) {
        setMobileViewMode('chat');
        return;
      }
      // Expanded panels aren't collapsible, so `collapse()` here is a no-op. Drop
      // expanded layout first; the expanded-state effect collapses the panel once
      // it becomes collapsible again.
      if (isExpanded) {
        pendingCloseRef.current = true;
        artifactPanelModel?.collapse();
        return;
      }
      const panel = artifactPanelRef.current as
        | { collapse?: () => void }
        | null
        | undefined;
      if (typeof panel?.collapse === 'function') {
        const openPercent =
          panelGroupRef.current?.getLayout()[1] ?? defaultArtifactPanelSize;
        animateWidthChange(openPercent, () => panel.collapse?.());
      }
    }, [
      isMobile,
      onRequestClose,
      isExpanded,
      artifactPanelModel,
      animateWidthChange,
      defaultArtifactPanelSize,
    ]);

    /**
     * Esc narrows the artifact back down, whichever way it was widened.
     *
     * Focus first: it is the wider of the two states and the only one reachable
     * on a phone, so it is not gated on `isMobile` the way the in-place expand
     * is. Without this the shortcut simply stopped applying once expanding
     * became focus — the tooltip still said `(Esc)`, and the key did nothing.
     */
    const closeFocusView = focusView?.onBack;
    useEffect(() => {
      const narrow =
        closeFocusView ??
        (isExpanded === true && isMobile === false
          ? toggleExpanded
          : undefined);
      if (narrow === undefined) {
        return;
      }

      const handleKeyDown = (event: KeyboardEvent): void => {
        if (event.defaultPrevented === true || event.key !== 'Escape') {
          return;
        }
        event.preventDefault();
        narrow();
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [closeFocusView, isExpanded, isMobile, toggleExpanded]);

    /**
     * The artifact list, read at call time rather than captured.
     *
     * `focusArtifact` below is published on the panel's context value, so its
     * identity decides that value's identity, and that value wraps the whole
     * artifact subtree — the focus view included. Capturing `artifacts` or
     * `focusView` in its closure re-mints it whenever either moves, and
     * `focusView` moves on *every* render of the chat page (it is built from
     * `useAction`'s result, which is a fresh object each time). That put a
     * re-render of every artifact-panel consumer on every streaming tick, for a
     * callback whose behaviour never changed. Same pattern as `artifactsRef` in
     * `chat-with-artifact-panel`.
     */
    const artifactsRef = useRef(artifacts);
    artifactsRef.current = artifacts;

    /**
     * The kind of an artifact this panel is holding, or `''` for one it is not.
     *
     * A URL needs the kind to reproduce an artifact, and the panel is what holds
     * the descriptors — so the resolution happens here rather than at every
     * caller. Stable, so the callbacks built on it can be too.
     */
    const artifactTypeOf = useCallback(
      (artifactId: string): string =>
        artifactsRef.current.find((a) => a.artifactId === artifactId)?.type ??
        '',
      []
    );

    /**
     * `focusView` as the chrome publishes it: the same object, with the kind
     * bound into the switcher so consumers keep a plain `(artifactId)` call.
     *
     * One home for that binding. `focusArtifact` reaches it through the ref
     * below rather than depending on it, since this changes with `focusView` and
     * that must not.
     */
    const focusViewChrome = useMemo(() => {
      if (focusView === undefined) {
        return undefined;
      }
      return {
        ...focusView,
        onSelectArtifact: (artifactId: string): void =>
          focusView.onSelectArtifact(artifactId, artifactTypeOf(artifactId)),
      };
    }, [focusView, artifactTypeOf]);

    const focusViewChromeRef = useRef(focusViewChrome);
    focusViewChromeRef.current = focusViewChrome;

    const focusArtifact = useCallback<
      ArtifactPanelContextValue['focusArtifact']
    >(
      (artifactId) => {
        // Once focused, an artifact is no longer a passive background tab — the
        // user engaged with it, so it should survive reference pruning (and stay
        // out of the background set even if the reference sync re-opens it).
        backgroundArtifactIdsRef.current.delete(artifactId);
        engagedArtifactIdsRef.current.add(artifactId);
        // In the focus view the pane *is* one artifact, named by the URL — so which
        // one is on screen is not this component's tab state to set, and
        // `activeTabFromFocusView` below overrules it on the very next render.
        // Focusing there means moving the address, the same thing the switcher
        // does.
        //
        // Without this a resource opened from a focused artifact — clicked in
        // a Library page filling the window, say — landed in the switcher and
        // nowhere else, and had to be picked out of the dropdown by hand. The
        // split had no such problem, which is why this looked like it only
        // half-worked.
        const inFocusView = focusViewChromeRef.current;
        if (inFocusView !== undefined) {
          inFocusView.onSelectArtifact(artifactId);
          return;
        }
        // Keep both states in sync so resizing past the mobile breakpoint
        // shows the same artifact in the desktop panel.
        setActiveTab(artifactId);
        if (isMobile) {
          setMobileViewMode('artifacts');
          return;
        }
        const panel = artifactPanelRef.current as
          | {
              expand?: (minSizeOverride?: number) => void;
              isCollapsed?: () => boolean;
            }
          | null
          | undefined;
        if (
          typeof panel?.expand === 'function' &&
          typeof panel?.isCollapsed === 'function' &&
          panel.isCollapsed()
        ) {
          const openPercent =
            lastOpenLayoutRef.current?.[1] ?? defaultArtifactPanelSize;
          // `openPercent` is handed to `expand` as well as to the width pin, or
          // the two disagree. Bare `expand()` restores the width the panel had
          // before it was collapsed and falls back to `minSize` when there is
          // none — and a split that *mounts* closed has none, so the first open
          // of every chat would land at the floor while the card was pinned at
          // the default. Passed the override, the library still prefers a
          // remembered width when it has one (`lastOpenLayoutRef` is that same
          // width), so reopening keeps wherever the user left the divider.
          animateWidthChange(
            openPercent,
            () => panel.expand?.(openPercent),
            // The reveal, so it settles before it sweeps.
            true
          );
        }
      },
      [isMobile, animateWidthChange, defaultArtifactPanelSize]
    );

    useImperativeHandle(ref, () => ({ focusArtifact }), [focusArtifact]);

    // Selecting a tab (dropdown / mobile picker) also counts as engaging with it,
    // so it graduates out of the background set and won't be pruned as a stale
    // reference tab.
    const selectArtifact = useCallback((artifactId: string) => {
      backgroundArtifactIdsRef.current.delete(artifactId);
      engagedArtifactIdsRef.current.add(artifactId);
      setActiveTab(artifactId);
    }, []);

    const openArtifact = useCallback<ArtifactPanelContextValue['openArtifact']>(
      (request) => {
        // Add the transient artifact (via the wrapper). When focus is requested
        // (the default), focus it — it arrives through `artifacts` on the next
        // render where auto-focus also catches it; setting the active tab here
        // covers the re-open (already present) case. When `focus: false`, record
        // it as a background artifact so it lands in the list without stealing the
        // active tab (see the auto-focus effect above).
        if (request.focus === false) {
          // Don't re-background a tab the user has already engaged with — its
          // graduation is sticky, so a resync must not expose it to pruning again.
          if (!engagedArtifactIdsRef.current.has(request.artifactId)) {
            backgroundArtifactIdsRef.current.add(request.artifactId);
          }
          onOpenArtifact?.(request);
          return;
        }
        backgroundArtifactIdsRef.current.delete(request.artifactId);
        engagedArtifactIdsRef.current.add(request.artifactId);
        onOpenArtifact?.(request);
        focusArtifact(request.artifactId);
      },
      [onOpenArtifact, focusArtifact]
    );
    // Opening is wired by the conversation-backed wrapper; without it
    // `openArtifact` above quietly does nothing.
    const canOpenArtifacts = onOpenArtifact !== undefined;

    // Full view is addressed by URL, so the URL decides — not the tab state,
    // which auto-focuses the newest artifact and would quietly show something
    // other than what the address bar says. Null when the id does not resolve,
    // which the focus-view branch reports rather than papering over: showing a
    // different artifact than the address bar names is the one outcome worse
    // than saying it is gone. Everything else on this page — the switcher, the
    // view stack — still wants a tab, so `effectiveActiveTab` below keeps the
    // usual fallback.
    const activeTabFromFocusView =
      focusView !== undefined &&
      artifacts.some((a) => a.artifactId === focusView.artifactId)
        ? focusView.artifactId
        : null;
    const effectiveActiveTab =
      activeTabFromFocusView ??
      (activeTab !== null && artifacts.some((a) => a.artifactId === activeTab)
        ? activeTab
        : artifacts.length > 0
        ? artifacts[artifacts.length - 1]?.artifactId ?? null
        : null);

    // Published for chat-scoped actions: Share in an empty chat shares this.
    const chatArtifactModel = useStatefullyAwareInScopeModel(ChatArtifactModel);
    useEffect(() => {
      chatArtifactModel?.setActiveArtifactId(effectiveActiveTab);
    }, [chatArtifactModel, effectiveActiveTab]);

    /**
     * The opener the chrome publishes, with the active artifact's kind bound in.
     *
     * The destination is a URL, and what a URL has to carry to reproduce an
     * artifact depends on what kind it is — the panel is the one holding the
     * descriptors, so it resolves that here rather than making every caller of
     * `openFocusView(artifactId)` do it.
     *
     * `undefined` only where it always was: a surface with no route to send an
     * artifact to, which is what makes the expand button fall back to its
     * in-place expand on the standalone editor pages.
     */
    const focusViewOpener = useMemo(() => {
      if (onOpenFocusView === undefined || effectiveActiveTab === null) {
        return undefined;
      }
      const artifact = artifacts.find(
        (a) => a.artifactId === effectiveActiveTab
      );
      if (artifact === undefined) {
        return undefined;
      }
      const { artifactId, type } = artifact;
      return (): void => onOpenFocusView(artifactId, type);
    }, [onOpenFocusView, artifacts, effectiveActiveTab]);

    const activeViewStack = useMemo((): readonly ArtifactPanelViewEntry[] => {
      if (effectiveActiveTab === null) {
        return [];
      }
      return viewStacks.get(effectiveActiveTab) ?? [];
    }, [viewStacks, effectiveActiveTab]);

    const pushView = useCallback<ArtifactPanelContextValue['pushView']>(
      (entry, options) => {
        if (effectiveActiveTab === null) {
          return;
        }
        setViewStacks((prev) => {
          const next = new Map(prev);
          const current = next.get(effectiveActiveTab) ?? [];
          next.set(effectiveActiveTab, pushViewEntry(current, entry, options));
          return next;
        });
      },
      [effectiveActiveTab]
    );

    const popView = useCallback<ArtifactPanelContextValue['popView']>(() => {
      if (effectiveActiveTab === null) {
        return;
      }
      setViewStacks((prev) => {
        const current = prev.get(effectiveActiveTab) ?? [];
        if (current.length === 0) {
          return prev;
        }
        const next = new Map(prev);
        next.set(effectiveActiveTab, popViewEntry(current));
        return next;
      });
    }, [effectiveActiveTab]);

    const resetViewStack = useCallback<
      ArtifactPanelContextValue['resetViewStack']
    >(() => {
      if (effectiveActiveTab === null) {
        return;
      }
      setViewStacks((prev) => {
        const current = prev.get(effectiveActiveTab) ?? [];
        if (current.length === 0) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(effectiveActiveTab);
        return next;
      });
    }, [effectiveActiveTab]);

    const closeArtifact = useCallback<
      ArtifactPanelContextValue['closeArtifact']
    >(
      (artifactId) => {
        backgroundArtifactIdsRef.current.delete(artifactId);
        engagedArtifactIdsRef.current.delete(artifactId);
        setViewStacks((prev) => {
          if (!prev.has(artifactId)) {
            return prev;
          }
          const next = new Map(prev);
          next.delete(artifactId);
          return next;
        });
        onRemoveArtifact?.(artifactId);
      },
      [onRemoveArtifact]
    );

    // Close an artifact only if it's still a passive background tab (surfaced via
    // openArtifact `focus: false` and never engaged). Tool/persisted artifacts and
    // user-focused tabs are never in the background set, so this can't dismiss a tab
    // the expander didn't open — that's what makes reference pruning safe.
    const closeBackgroundArtifact = useCallback<
      ArtifactPanelContextValue['closeBackgroundArtifact']
    >(
      (artifactId) => {
        if (!backgroundArtifactIdsRef.current.has(artifactId)) {
          return;
        }
        closeArtifact(artifactId);
      },
      [closeArtifact]
    );

    /**
     * The panel's close control (the header "X"). This only dismisses the panel
     * — it never removes the artifact from the chat. A transient panel clears its
     * artifact (`onRequestClose`, still a "close" for a host that has nothing to
     * keep); otherwise the panel just collapses (desktop) or returns to the chat
     * tab (mobile). Removing an artifact from the conversation is a separate,
     * explicit action, not a side effect of closing the panel.
     */
    const closeActiveArtifact = useCallback(() => {
      handleClose();
    }, [handleClose]);

    // Pop the focused artifact's drill-in stack down to `depth` entries — the
    // unified breadcrumb's crumb clicks (depth 0 = back to the artifact itself).
    const popToViewDepth = useCallback(
      (depth: number) => {
        if (effectiveActiveTab === null) {
          return;
        }
        setViewStacks((prev) => {
          const current = prev.get(effectiveActiveTab) ?? [];
          if (current.length <= depth) {
            return prev;
          }
          const next = new Map(prev);
          if (depth <= 0) {
            next.delete(effectiveActiveTab);
          } else {
            next.set(effectiveActiveTab, current.slice(0, depth));
          }
          return next;
        });
      },
      [effectiveActiveTab]
    );

    // Artifacts are still open but the split was collapsed shut, leaving nothing
    // on screen pointing at them — the chat composer offers the way back in.
    // Desktop only: mobile always keeps the artifact pills in its top bar.
    const isHidden = !isMobile && artifacts.length > 0 && isPanelCollapsed;

    // `true` off the Coco chat page, where there is no header toggle and the
    // card has always shown on its own terms.
    const indexVisibility = useArtifactsIndexVisibility();
    const isIndexCardVisible = indexVisibility?.isVisible ?? true;

    const revealArtifacts = useCallback(() => {
      if (effectiveActiveTab === null) {
        return;
      }
      focusArtifact(effectiveActiveTab);
    }, [effectiveActiveTab, focusArtifact]);

    const artifactPanelContextValue = useMemo<ArtifactPanelContextValue>(
      () => ({
        isExpanded,
        toggleExpanded,
        expand,
        canExpand,
        isHidden,
        revealArtifacts,
        focusArtifact,
        openArtifact,
        canOpenArtifacts,
        closeArtifact,
        closeBackgroundArtifact,
        pushView,
        popView,
        resetViewStack,
        canPopView: activeViewStack.length > 0,
        viewStack: activeViewStack,
        activeArtifactId: effectiveActiveTab,
      }),
      [
        isExpanded,
        toggleExpanded,
        expand,
        canExpand,
        isHidden,
        revealArtifacts,
        focusArtifact,
        openArtifact,
        canOpenArtifacts,
        closeArtifact,
        closeBackgroundArtifact,
        pushView,
        popView,
        resetViewStack,
        activeViewStack,
        effectiveActiveTab,
      ]
    );

    // Unified in-panel breadcrumb data, provided around the artifact views only
    // (desktop). Null on mobile, where the pill tabs handle switching.
    const panelUnifiedChrome = useMemo<PanelUnifiedChrome>(
      () => ({
        artifacts,
        activeArtifactId: effectiveActiveTab,
        // In the focus view the switcher moves the URL; the tab state it would
        // otherwise set is not what decides the page. See `focusView`.
        selectArtifact: focusViewChrome?.onSelectArtifact ?? selectArtifact,
        viewStack: activeViewStack,
        popToViewDepth,
        closeActiveArtifact,
        host,
        // The chrome publishes the plain `(artifactId)` switcher: its consumers
        // hold no descriptors, so resolving the kind is this panel's job.
        ...(focusViewChrome !== undefined && { focusView: focusViewChrome }),
        openFocusView: focusViewOpener,
      }),
      [
        artifacts,
        effectiveActiveTab,
        selectArtifact,
        activeViewStack,
        popToViewDepth,
        closeActiveArtifact,
        host,
        focusViewChrome,
        focusViewOpener,
      ]
    );

    const hasArtifacts = artifacts.length > 0;
    // The split mounts with the artifact panel already shut. Read by the panels'
    // `defaultSize` below, which is the only value the group takes late enough
    // to be the last word on the opening layout — see the effect that follows.
    const startsClosed = !autoOpenOnArtifact && !returningFromFocusRef.current;

    /**
     * The width the artifact panel opens at.
     *
     * Returning from focus it is wherever the reader last left the divider, not
     * the default. The split is torn down while focused and built again on the
     * way out, so its `defaultSize` decides the width — and handing back a
     * panel snapped to the default throws away a deliberate adjustment the
     * reader made a moment earlier.
     *
     * `lastOpenLayoutRef` is the right source and needs no new bookkeeping: it
     * already records every layout in which the panel had width, and it stops
     * updating while focused because the group that reports layouts is not
     * mounted then. So it still holds the pre-focus split.
     */
    const openArtifactWidth = returningFromFocusRef.current
      ? lastOpenLayoutRef.current?.[1] ?? defaultArtifactPanelSize
      : defaultArtifactPanelSize;

    // The first artifact of a chat mounts the whole split, which otherwise
    // appears at full width in a single frame. Start it closed *before paint*
    // (a layout effect, so there is no frame at full width to see) and animate
    // out to the default on the next one, so an artifact the agent just created
    // slides in rather than materialising.
    //
    // `isOpeningRef` covers the side effect of starting at zero: that width
    // trips the panel's own `onCollapse`, which would mark the panel hidden and
    // flash the artifacts index open for a frame on the way in.
    //
    // None of this runs when the panel is not meant to open at all — that case
    // is `startsClosed` below, which the split's *default sizes* carry rather
    // than an imperative call from here. It has to be: mounting the artifact
    // panel registers it with the group, and the group recomputes its layout
    // from those defaults in a commit after this effect, so a `setLayout` shut
    // written here is overwritten by the very registration that created the
    // panel. That is the bug this comment exists to stop being reintroduced.
    const isOpeningRef = useRef(false);
    const hasAnimatedOpenRef = useRef(false);
    useLayoutEffect(() => {
      if (isMobile || !hasArtifacts) {
        hasAnimatedOpenRef.current = false;
        return;
      }
      if (hasAnimatedOpenRef.current) {
        return;
      }
      hasAnimatedOpenRef.current = true;
      if (startsClosed) {
        // The library fires `onCollapse` for a panel that registers at its
        // collapsed size, but this is what the artifacts index card keys off,
        // so it is set outright rather than waited on.
        setIsPanelCollapsed(true);
        return;
      }
      const group = panelGroupRef.current;
      if (group === null) {
        return;
      }
      isOpeningRef.current = true;
      group.setLayout([100, 0]);
      const rafId = requestAnimationFrame(() => {
        animateWidthChange(defaultArtifactPanelSize, () => {
          group.setLayout(defaultSplitLayout);
        });
        isOpeningRef.current = false;
      });
      return () => {
        cancelAnimationFrame(rafId);
        isOpeningRef.current = false;
      };
    }, [
      isMobile,
      hasArtifacts,
      startsClosed,
      defaultSplitLayout,
      defaultArtifactPanelSize,
      animateWidthChange,
    ]);
    /**
     * Closing focus narrows the artifact back into the split instead of cutting
     * to it.
     *
     * The mirror of the opening animation above, and it has to be a separate
     * one: that runs once per chat and is long spent by the time anyone focuses
     * anything. This starts where focus left the screen — the artifact filling
     * the pane — and animates to the width the reader had the divider at, so
     * the conversation slides back in beside it rather than appearing.
     *
     * A layout effect, so the wide starting layout is in place before the
     * browser paints; a frame later the transition class is on and the widths
     * move. `defaultSize` still carries the *resting* width, so a close that
     * never gets to animate still settles in the right place.
     */
    useLayoutEffect(() => {
      if (!animateOutOfFocusRef.current) {
        return;
      }
      animateOutOfFocusRef.current = false;
      const group = panelGroupRef.current;
      if (group === null || isMobile || !hasArtifacts) {
        setIsClosingFocus(false);
        return;
      }
      group.setLayout([0, 100]);
      let settleId: number | undefined;
      const rafId = requestAnimationFrame(() => {
        animateWidthChange(openArtifactWidth, () => {
          group.setLayout([100 - openArtifactWidth, openArtifactWidth]);
        });
        // The chat pane's floor goes back up only once it is past it again.
        settleId = window.setTimeout(
          () => setIsClosingFocus(false),
          ARTIFACT_PANEL_EXPAND_MS
        );
      });
      return () => {
        cancelAnimationFrame(rafId);
        if (settleId !== undefined) {
          window.clearTimeout(settleId);
        }
      };
    }, [
      focusedArtifactId,
      isMobile,
      hasArtifacts,
      openArtifactWidth,
      animateWidthChange,
    ]);

    // Double-click the seam to put the split back where it started. The
    // conventional divider gesture, and undiscoverable on its own — which is why
    // it ships with the hint rather than before it.
    const resetSplit = useCallback(() => {
      const group = panelGroupRef.current;
      if (group === null || isExpanded) {
        return;
      }
      animateWidthChange(defaultArtifactPanelSize, () => {
        group.setLayout(defaultSplitLayout);
      });
    }, [
      isExpanded,
      animateWidthChange,
      defaultArtifactPanelSize,
      defaultSplitLayout,
    ]);

    const selectedArtifact = useMemo(() => {
      if (effectiveActiveTab === null) {
        return null;
      }
      return (
        artifacts.find(
          (artifact) => artifact.artifactId === effectiveActiveTab
        ) ?? null
      );
    }, [artifacts, effectiveActiveTab]);

    // If on mobile and the user is viewing artifacts but the list goes empty,
    // fall back to the primary tab.
    useEffect(() => {
      if (isMobile && mobileViewMode === 'artifacts' && !hasArtifacts) {
        setMobileViewMode('chat');
      }
    }, [isMobile, mobileViewMode, hasArtifacts]);

    // Full view: one artifact, the whole page. Ahead of the mobile branch on
    // purpose — a shared link opened on a phone is the same page, and the pill
    // tabs it would otherwise get are a switcher between things this URL does
    // not name.
    if (focusView !== undefined) {
      return (
        <ArtifactPanelProvider value={artifactPanelContextValue}>
          <ResourceNavigatorBridge />
          <PanelUnifiedChromeProvider value={panelUnifiedChrome}>
            <div className="relative flex h-full min-h-0 w-full flex-col">
              {/* Keyed to the id the URL names, not to `effectiveActiveTab`,
                  which falls back to the newest artifact when that id does not
                  resolve. Here that fallback would quietly show one artifact
                  while the address bar named another. */}
              {activeTabFromFocusView !== null ? (
                // The artifact fades up rather than appearing: opening a full
                // page is a route change, so the split is gone in one frame and
                // whatever lands next lands hard. `animate-fade-in` is the app's
                // own 300ms, near enough the panel's 320ms that the two read as
                // the same gesture.
                //
                // Only the artifact. The skeleton beneath it is already a soft
                // pulse and usually never paints — animating it too would put a
                // second fade in front of a chat that had cached its artifacts
                // and was ready immediately.
                <div className="flex min-h-0 flex-1 flex-col motion-safe:animate-fade-in">
                  <MountedArtifactViews
                    artifacts={artifacts}
                    activeArtifactId={activeTabFromFocusView}
                    onClose={focusView.onBack}
                  />
                </div>
              ) : artifactsLoading ? (
                <ArtifactFocusViewLoading />
              ) : (
                <ArtifactFocusViewMissing onBack={focusView.onBack} />
              )}
            </div>
          </PanelUnifiedChromeProvider>
        </ArtifactPanelProvider>
      );
    }

    if (isMobile) {
      return (
        <ArtifactPanelProvider value={artifactPanelContextValue}>
          <ResourceNavigatorBridge />
          <MobileArtifactTabs
            hasArtifacts={hasArtifacts}
            viewMode={mobileViewMode}
            onViewModeChange={setMobileViewMode}
            primary={children}
            primaryTabIcon={primaryTabIcon}
            artifacts={artifacts}
            selectedArtifactId={effectiveActiveTab}
            onSelectArtifact={selectArtifact}
            selectedArtifact={selectedArtifact}
            onCloseArtifact={closeActiveArtifact}
          />
        </ArtifactPanelProvider>
      );
    }

    const renderArtifactPanelContent = () => (
      <div
        ref={contentWrapperRef}
        className={cn(
          // `py-3` rather than the `py-6` this used to carry. 24px above and
          // below read as gap rather than as float, and it was 48px of height
          // the artifact could have been using — the pane is the one surface
          // here that is always short of room. The bar above ends in a border
          // and the content panel below carries its own margin, so both edges
          // are already separated; the card only needs enough left for its own
          // edge and shadow to register.
          //
          // No right padding at all. This edge meets `.v1-content-panel`, which
          // already holds the artifact off the window with its own 0.5rem
          // margin and border — so the `pr-6` (19.5px at this app's 13px root)
          // that used to sit here was a second float over the first, and the
          // surplus read as a dead strip down the full height of the pane.
          //
          // The card's own shadow is clipped on this side as a result, since
          // the content panel is `overflow: hidden`. That is the trade: the
          // shadow was only ever visible in the strip being removed.
          //
          // The left edge is a different problem — see `md:pl-2`.
          'md:py-3',
          artifactCardResizeHandle === true
            ? isExpanded
              ? 'pointer-events-none flex h-full min-h-0 flex-col overflow-visible md:pl-10'
              : // 8px, down from 16px. This edge does not meet the window, it
                // meets the seam — and the seam is invisible, so the gap *is*
                // what the eye reads as the divider. At 16px there was visibly
                // a gap and then, separately, a card: the thing you grab and
                // the thing it moves looked like two places.
                //
                // Not zero. The card keeps its own border and shadow, and they
                // need somewhere to land.
                //
                // Paired with `w-2`/`-mr-2` on the handle
                // (`ui/resizable.tsx`), which sizes itself to this gutter so
                // the drag region covers the gap and stops at the card. Change
                // this and change that, or the resize cursor starts appearing
                // over the card's own edge.
                'pointer-events-none flex h-full min-h-0 flex-col overflow-visible md:pl-2'
            : isExpanded
            ? 'flex h-full min-h-0 flex-col md:pl-10'
            : 'flex h-full min-h-0 flex-col md:pl-6',
          isLayoutAnimating && 'transition-[padding] duration-[320ms] ease-out'
        )}
      >
        <div
          data-slot="chat-artifact-panel"
          className="pointer-events-auto flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <div className="h-full flex flex-col min-h-0">
            {/* The picker + close row is gone: each artifact's own bar (or the
                bare-type fallback) now renders one unified breadcrumb that
                doubles as the artifact switcher. */}
            <div className="flex-1 min-h-0 overflow-hidden relative">
              <PanelUnifiedChromeProvider value={panelUnifiedChrome}>
                <MountedArtifactViews
                  artifacts={artifacts}
                  activeArtifactId={effectiveActiveTab}
                  onClose={closeActiveArtifact}
                />
              </PanelUnifiedChromeProvider>
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <ArtifactPanelProvider value={artifactPanelContextValue}>
        <ResourceNavigatorBridge />
        <ResizablePanelGroup
          ref={panelGroupRef}
          direction="horizontal"
          onLayout={handlePanelLayout}
          className={cn(
            'h-full w-full',
            isLayoutAnimating &&
              '[&_[data-panel]]:transition-[flex-grow] [&_[data-panel]]:duration-[320ms] [&_[data-panel]]:ease-out',
            isManualResizing && '[&_[data-panel]]:!transition-none'
          )}
        >
          <ResizablePanel
            id="artifact-primary-panel"
            defaultSize={
              hasArtifacts && !startsClosed ? 100 - openArtifactWidth : 100
            }
            minSize={isExpanded || isClosingFocus ? 0 : 30}
            className="min-h-0 min-w-0 overflow-hidden"
          >
            <div
              className={cn(
                // `relative` anchors the artifacts index card to this pane's
                // right edge — the edge the artifact panel collapsed away from.
                'relative flex h-full min-h-0 flex-col transition-opacity duration-[320ms] ease-out',
                isExpanded && 'pointer-events-none opacity-0',
                // Published, not applied: the chat's own boxes read this and
                // give up the room, so the conversation's scroll container
                // stays full width and its bar stays at the window's edge.
                //
                // Only while the card is actually on the pane. Room reserved
                // for something the user has dismissed is just a margin they
                // cannot explain — the conversation re-centres in the whole
                // pane instead, and the gutter animates back in when the card
                // returns.
                isHidden && isIndexCardVisible && ARTIFACTS_INDEX_GUTTER
              )}
              aria-hidden={isExpanded}
            >
              {children}
              <ArtifactsIndexCard
                artifacts={artifacts}
                onSelect={focusArtifact}
              />
            </div>
          </ResizablePanel>
          {hasArtifacts && (
            <>
              <ResizableHandle
                // The artifact-card seam is deliberately unmarked; only the
                // bordered `default` split carries a grip. What replaces the
                // grip is the hint the handle shows on approach, which lists
                // this reset alongside the drag.
                withHandle={artifactCardResizeHandle !== true}
                disabled={isExpanded || isLayoutAnimating}
                onDragging={setIsManualResizing}
                onDoubleClick={resetSplit}
                variant={
                  artifactCardResizeHandle === true
                    ? 'artifact-card'
                    : 'default'
                }
                className={cn(
                  'transition-[opacity,width] duration-[320ms] ease-out',
                  (isExpanded || isLayoutAnimating) &&
                    'pointer-events-none w-0 opacity-0'
                )}
              />
              <ResizablePanel
                ref={artifactPanelRef}
                id="artifact-side-panel"
                // Zero is a legal default *because* the panel is collapsible
                // with `collapsedSize={0}`; the library only rejects a default
                // under `minSize` when it is not the collapsed size.
                defaultSize={startsClosed ? 0 : openArtifactWidth}
                minSize={minArtifactPanelSize}
                // Explicit, though 0 is the library default: it is what makes
                // "dragged below the floor" resolve to closed rather than
                // clamped at the floor.
                collapsedSize={0}
                collapsible={!isExpanded}
                onCollapse={() => {
                  // Zero width during the intro animation is a starting point,
                  // not the user closing anything.
                  if (isOpeningRef.current) {
                    return;
                  }
                  setIsPanelCollapsed(true);
                }}
                onExpand={() => setIsPanelCollapsed(false)}
                className={
                  artifactCardResizeHandle === true
                    ? 'min-h-0 min-w-0 overflow-visible pointer-events-none'
                    : 'min-h-0 min-w-0'
                }
              >
                {renderArtifactPanelContent()}
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      </ArtifactPanelProvider>
    );
  }
);

ArtifactSidePanelInner.displayName = 'ArtifactSidePanelInner';

/**
 * One tab in the mobile switcher: a rounded pill carrying an icon and a word.
 *
 * Both tabs are labelled. The chat side used to be a bare glyph beside a row of
 * named artifact pills, which made it read as a back button rather than as one
 * of a pair — and on the artifacts side there was nothing to go back *to* but
 * that glyph.
 */
const MOBILE_TAB_BASE =
  'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const mobileTabClass = (active: boolean): string =>
  cn(
    MOBILE_TAB_BASE,
    active
      ? 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80'
      : 'border-input bg-background text-foreground hover:border-border hover:bg-muted/60'
  );

/** The `N` beside a tab's label. Only ever drawn at two or more. */
const MobileTabCount: React.FC<{ count: number; active: boolean }> = ({
  count,
  active,
}) => (
  <span
    className={cn(
      'shrink-0 rounded px-1 text-[10px] font-bold leading-4',
      active
        ? 'bg-secondary-foreground/10 text-secondary-foreground/80'
        : 'bg-muted text-muted-foreground'
    )}
  >
    {count}
  </span>
);

/**
 * The artifacts half of the mobile switcher, which is two controls wearing one
 * pill depending on where you are.
 *
 * From the chat it is a destination — "Artifacts 2" — and one tap takes you to
 * whichever artifact was last open. Standing on an artifact it becomes that
 * artifact's switcher, naming what is on screen and opening the list of the
 * rest: the same control the desktop bar's title is, in the same place.
 *
 * It replaces a horizontally scrolling row that held one pill per artifact.
 * That row was fine at two and unusable past four — the pills the user had not
 * scrolled to were invisible, there was no count to say how many were out
 * there, and the row pushed the close button off the end of the bar. This one
 * is a fixed width whatever the conversation produces, and it is the only place
 * on mobile that has ever said which artifact you are reading.
 */
const MobileArtifactsTab: React.FC<{
  artifacts: ArtifactDescriptor[];
  selectedArtifactId: string | null;
  selected: ArtifactDescriptor | null;
  showingArtifact: boolean;
  onSelect: (artifactId: string) => void;
  onOpen: () => void;
}> = ({
  artifacts,
  selectedArtifactId,
  selected,
  showingArtifact,
  onSelect,
  onOpen,
}) => {
  const dirtyResourceIds = useDirtyArtifactResourceIds();
  const count = artifacts.length;

  if (!showingArtifact || selected === null) {
    return (
      <button
        type="button"
        onClick={onOpen}
        aria-pressed={false}
        className={mobileTabClass(false)}
      >
        <LayoutGrid className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="leading-none">Artifacts</span>
        {count > 1 ? <MobileTabCount count={count} active={false} /> : null}
      </button>
    );
  }

  // One artifact and it is already on screen: there is nothing to switch to, so
  // the pill states where you are rather than offering an empty menu.
  if (count === 1) {
    return (
      <span className={cn(mobileTabClass(true), 'min-w-0')}>
        <ArtifactIcon
          artifactType={selected.type}
          className="size-3.5 shrink-0 text-secondary-foreground/80"
        />
        <span className="min-w-0 max-w-[10rem] truncate leading-none">
          <ArtifactLabel artifact={selected} showDirtyIndicator />
        </span>
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(mobileTabClass(true), 'min-w-0 cursor-pointer')}
        aria-label={`Switch artifact (${count} open)`}
      >
        <ArtifactIcon
          artifactType={selected.type}
          className="size-3.5 shrink-0 text-secondary-foreground/80"
        />
        <span className="min-w-0 max-w-[9rem] truncate text-left leading-none">
          <ArtifactLabel artifact={selected} showDirtyIndicator />
        </span>
        <MobileTabCount count={count} active />
        <ChevronDown className="size-3 shrink-0 stroke-[2.5] text-secondary-foreground/70" />
      </DropdownMenuTrigger>
      {/* `max-w` against the viewport rather than a rem cap: this menu opens on
          a phone, where a 26rem panel menu would simply run off the screen. */}
      <DropdownMenuContent
        align="start"
        className="max-h-[60dvh] w-max min-w-[14rem] max-w-[calc(100vw-2rem)] overflow-y-auto"
      >
        {artifacts.map((artifact) => (
          <DropdownMenuItem
            key={artifact.artifactId}
            onSelect={() => onSelect(artifact.artifactId)}
            className="gap-2"
          >
            <Check
              className={cn(
                'h-3.5 w-3.5 shrink-0',
                artifact.artifactId === selectedArtifactId
                  ? 'opacity-100'
                  : 'opacity-0'
              )}
            />
            <ArtifactIcon
              artifactType={artifact.type}
              size="small"
              className="shrink-0 text-muted-foreground"
            />
            <span className="min-w-0 flex-1 truncate">
              <ArtifactLabel artifact={artifact} />
            </span>
            {artifactMatchesDirtyResource(artifact, dirtyResourceIds) ? (
              <ArtifactDirtyDot />
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const MobileArtifactTabs: React.FC<{
  hasArtifacts: boolean;
  viewMode: 'chat' | 'artifacts';
  onViewModeChange: (mode: 'chat' | 'artifacts') => void;
  primary: React.ReactNode;
  primaryTabIcon?: React.ReactNode;
  artifacts: ArtifactDescriptor[];
  selectedArtifactId: string | null;
  onSelectArtifact: (artifactId: string) => void;
  selectedArtifact: ArtifactDescriptor | null;
  onCloseArtifact: () => void;
}> = ({
  hasArtifacts,
  viewMode,
  onViewModeChange,
  primary,
  primaryTabIcon,
  artifacts,
  selectedArtifactId,
  onSelectArtifact,
  selectedArtifact,
  onCloseArtifact,
}) => {
  const archivedArtifactIds = useArchivedArtifactIds();
  const showingArtifact =
    hasArtifacts && viewMode === 'artifacts' && selectedArtifact !== null;

  // The pill row is mobile's switcher, so it hides archived artifacts on the
  // same terms as the desktop bar's menu — all but the one currently selected,
  // which stays so the row does not drop the pill you are standing on. The
  // views below keep every artifact mounted regardless; the selected archived
  // one renders its notice through the access gate.
  const pillArtifacts =
    archivedArtifactIds.size === 0
      ? artifacts
      : artifacts.filter(
          (artifact) =>
            !archivedArtifactIds.has(artifact.artifactId) ||
            artifact.artifactId === selectedArtifactId
        );

  return (
    <div className="h-full w-full flex flex-col min-h-0">
      {hasArtifacts ? (
        // Two tabs and a close, at a width that does not move. Whatever the
        // conversation produces, this row holds: the artifact count lives in a
        // badge rather than in the number of pills.
        <div className="shrink-0 border-b bg-background px-4 py-2">
          <div className="flex items-center gap-2">
            {/* Keyed to what is actually rendered below, not to `viewMode`:
                asking for artifacts while none resolves leaves the chat on
                screen, and the row has to say so rather than light the tab you
                did not get. */}
            <button
              type="button"
              onClick={() => onViewModeChange('chat')}
              aria-pressed={!showingArtifact}
              className={mobileTabClass(!showingArtifact)}
            >
              {primaryTabIcon ?? (
                <MessageSquareIcon className="size-3.5 shrink-0" />
              )}
              <span className="leading-none">Chat</span>
            </button>
            <MobileArtifactsTab
              artifacts={pillArtifacts}
              selectedArtifactId={selectedArtifactId}
              selected={showingArtifact ? selectedArtifact : null}
              showingArtifact={showingArtifact}
              onSelect={(id) => {
                onSelectArtifact(id);
                onViewModeChange('artifacts');
              }}
              onOpen={() => {
                // Nothing picked yet — the newest artifact is what the desktop
                // panel opens onto too.
                if (selectedArtifactId === null) {
                  const newest = pillArtifacts[pillArtifacts.length - 1];
                  if (newest !== undefined) {
                    onSelectArtifact(newest.artifactId);
                  }
                }
                onViewModeChange('artifacts');
              }}
            />
            {viewMode === 'artifacts' ? (
              <ArtifactClose
                className="ml-auto shrink-0"
                aria-label="Close artifact"
                onClick={onCloseArtifact}
              />
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="flex-1 min-h-0 relative">
        <div
          className={showingArtifact ? 'h-full w-full hidden' : 'h-full w-full'}
        >
          {/* Symmetric with the artifacts: the page releases targeting while an
              artifact is up, and takes it back when the artifact closes. */}
          <ModelTargetScope active={!showingArtifact}>
            {primary}
          </ModelTargetScope>
        </div>
        {hasArtifacts ? (
          // The same slot the desktop split stamps on its artifact pane, so the
          // artifact gets the same white ground here that it has there (see
          // `[data-slot='chat-artifact-panel']` in v1.css). Without it the
          // artifact sat on the app's warm canvas on a phone and on white
          // everywhere else — and on a phone the ground is the only thing
          // distinguishing the artifact from the conversation it replaced,
          // since there is no split to see it beside.
          //
          // The card chrome that slot also carries — radius, border, shadow —
          // is gated to `md` in the stylesheet, so nothing here draws a card
          // edge across a screen the artifact already fills.
          <div
            data-slot="chat-artifact-panel"
            className={
              showingArtifact ? 'absolute inset-0' : 'absolute inset-0 hidden'
            }
            aria-hidden={!showingArtifact}
          >
            <MountedArtifactViews
              artifacts={artifacts}
              activeArtifactId={selectedArtifactId}
              onClose={onCloseArtifact}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Keeps every open artifact mounted so editor state and in-scope models survive
 * tab switches (needed for multi-artifact Ctrl+S dirty detection). Inactive
 * views are hidden rather than unmounted.
 */
const MountedArtifactViews: React.FC<{
  artifacts: ArtifactDescriptor[];
  activeArtifactId: string | null;
  onClose: () => void;
}> = ({ artifacts, activeArtifactId, onClose }) => (
  <>
    {artifacts.map((artifact) => {
      const isActive = artifact.artifactId === activeArtifactId;
      return (
        <div
          key={artifact.artifactId}
          className={
            isActive ? 'h-full w-full' : 'h-full w-full absolute inset-0 hidden'
          }
          aria-hidden={!isActive}
        >
          <SingleArtifactView
            artifact={artifact}
            onClose={onClose}
            active={isActive}
          />
        </div>
      );
    })}
  </>
);

/** Claims a stack entry pushed by an `<ArtifactLink>`; see {@link resolveDeepestView}. */
const matchArtifactLink = (payload: unknown): ArtifactLinkTarget | null =>
  isArtifactLinkViewPayload(payload) ? payload.link : null;

const SingleArtifactView: React.FC<{
  artifact: ArtifactDescriptor;
  onClose: () => void;
  /** True when this is the artifact on screen; see {@link ModelTargetScope}. */
  active?: boolean;
}> = ({ artifact, onClose, active = true }) => {
  const Renderer = getArtifactRenderer(artifact.type);
  const artifactPanel = useArtifactPanel();
  const unifiedChrome = usePanelUnifiedChrome();

  // Only the focused artifact owns the panel's view stack, so a hidden tab never
  // reads another tab's pushed link. The deepest link wins rather than the top
  // entry: the link's own body (a run graph, say) pushes its drill-ins onto this
  // same stack, and those belong to the link — matching only the top handed the
  // panel back to the host artifact as soon as the user drilled one level in.
  const viewStack = artifactPanel?.viewStack ?? [];
  const activeLink = active
    ? resolveDeepestView(viewStack, matchArtifactLink)
    : null;

  const content = (
    <Artifact className="h-full flex flex-col min-h-0 !rounded-none !border-none bg-transparent shadow-none">
      <ArtifactContent className="flex-1 min-h-0 overflow-auto">
        {Renderer !== null ? (
          <Renderer artifact={artifact} onClose={onClose} />
        ) : (
          <ArtifactFallback type={artifact.type} />
        )}
      </ArtifactContent>
    </Artifact>
  );

  /**
   * The panel draws the bar, always, above whatever the artifact turns out to
   * be.
   *
   * It used to draw one only for "bare" types (charts, workflow-node configs)
   * and leave the rest to their own `ResourcePageShell`, on the strength of a
   * per-type capability flag on the resource kind. That was a promise made
   * before anything had been fetched, and the states where the page never
   * mounts are exactly the states that broke it: a resource the viewer has no
   * access to, one that has been archived, a skeleton whose access probe never
   * resolves. Each of those replaces the page — and with it the shell, and with
   * it the only bar on screen. What was left was a pane with no switcher, no
   * expand and no hide, which the user could not get out of at all.
   *
   * So the header stops depending on the body. `UnifiedPanelBar` claims itself
   * over its own subtree, so a page that does mount its shell underneath draws
   * no second bar and portals its actions up into this one — the same path a
   * nested shell has always taken when drilling workflow into agent.
   *
   * Null chrome means this is not the artifact panel (mobile's tabs, a
   * standalone editor's transient panel), where the bar belongs to whatever is
   * hosting it instead.
   */
  const chromedContent =
    unifiedChrome !== null ? (
      <div className="flex h-full min-h-0 w-full flex-col">
        <UnifiedPanelBar>
          <div className="flex-1 min-h-0 overflow-hidden">{content}</div>
        </UnifiedPanelBar>
      </div>
    ) : (
      content
    );

  // Everything shares one actions scope, so an artifact of the same type as its
  // host page would otherwise fight it for targeting. Visibility decides
  // instead: only what is on screen is the model of its type.
  return (
    <ModelTargetScope active={active}>
      {activeLink !== null ? (
        // The artifact stays mounted so its editor state and in-scope models —
        // unsaved workflow edits, most of all — survive the trip into the link
        // and back out again.
        <div className="h-full w-full flex flex-col min-h-0">
          <div className="hidden">{content}</div>
          <div className="flex-1 min-h-0">
            <ArtifactLinkStackView
              link={activeLink.value}
              nested={activeLink.nested}
              onBack={() => artifactPanel?.popView()}
            />
          </div>
        </div>
      ) : (
        chromedContent
      )}
    </ModelTargetScope>
  );
};
